import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import {
  calculateRemainingResource,
  getDefaultValueCustomResource,
  getMergePackageName,
  getPackageName,
} from '@src/services/package';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useUserInfo from '@src/hooks/useTtsUser';
import route from '@src/constants/route';
import { PACKAGE_TYPE } from '@src/constants/package';
import PackageContainer from './components/PackageContainer';
import PackageV2 from './PackageV2';

const SubscriptionCustomAndV2Item = ({
  packageInfo = {},
  usingOrder = {},
  totalRemainingCredits,
  isMobile,
}) => {
  const { i18n } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { t, language } = i18n;
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { lockCharacters = 0 } = user;
  const { packageExpiryDate } = usingOrder;
  const { type } = packageInfo;
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser || {};
  const { oneTime } = studio || {};

  const useLockCustom = getFeatureValue(FEATURE_KEYS.LOCK_CUSTOM_CREDITS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const isLockedCustom =
    usingOrder.type === PACKAGE_TYPE.STUDIO &&
    useLockCustom &&
    ttsUser?.studio?.custom?.remainingCredits &&
    ttsUser?.studio?.custom?.isLocked;

  const migrateOldPkgToV3 = getFeatureValue(
    FEATURE_KEYS.MIGRATE_OLD_PKG_TO_V3,
    { userId: user.id },
  );

  const oneTimeExpiryDate = oneTime?.packageExpiryDate || packageExpiryDate;
  const hasOneTimeExpired =
    oneTimeExpiryDate && moment(oneTimeExpiryDate).isBefore(moment());
  const isLockedOneTimeCredit = hasOneTimeExpired && !!lockCharacters;

  const {
    defaultCredits = 0,
    defaultDevices = 0,
    defaultVoiceClonings = 0,
  } = getDefaultValueCustomResource(packageInfo?.customResource) || {};

  const handleRedirectToBuyPackage = () => {
    const redirectUrl = `${route.PAYMENT}?package=${packageInfo?.code}&credits=${defaultCredits}&devices=${defaultDevices}&voiceClonings=${defaultVoiceClonings}`;
    history.push(redirectUrl);
  };

  const remainingResource = calculateRemainingResource(
    type,
    user,
    hasOneTimeExpired,
  );

  const renderPackageButton = () => (
    <Button
      fullWidth
      className="buy-more-btn"
      onClick={handleRedirectToBuyPackage}
      data-id="button-buy"
      data-current-package-code={packageInfo?.code}
    >
      {t('buyNow')}
    </Button>
  );

  return (
    <>
      <PackageContainer
        packageName={getPackageName(
          packageInfo?.name,
          packageInfo?.code,
          language,
          t,
          true,
          migrateOldPkgToV3,
        )}
        productType={t(`${getMergePackageName(type)}`)}
        renderButton={renderPackageButton}
        packageDetail={
          <>
            <Box className="package-wrapper">
              <Typography className="title">{t('currentPlan')}</Typography>
              <PackageV2
                credits={totalRemainingCredits}
                packageExpiryDate={packageExpiryDate}
                isMobile={isMobile}
                isLocked={isLockedCustom}
                isCustomPackage
              />
            </Box>
            <Box className="package-wrapper">
              <Typography className="title">
                {`${t('oldPlan')} `}
                {oneTime?.packageCode && (
                  <span className="old-plan-package-name">
                    {`(${getPackageName(
                      null,
                      oneTime?.packageCode,
                      language,
                      t,
                      true,
                      migrateOldPkgToV3,
                    )})`}
                  </span>
                )}
              </Typography>
              <PackageV2
                isLocked={isLockedOneTimeCredit}
                credits={remainingResource}
                packageExpiryDate={oneTimeExpiryDate}
                isMobile={isMobile}
              />
            </Box>
          </>
        }
        isMobile={isMobile}
      />
    </>
  );
};

export default SubscriptionCustomAndV2Item;
